// src/components/pages/AccountPage.js
import React, { useState, useEffect } from 'react';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebase-config';
import { useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';

/* ---------------- helper ---------------- */
const API_BASE =
  process.env.REACT_APP_API_URL || 'http://localhost:4242';

export default function AccountPage({ user, accountType, credits }) {
  /* ───── profile / subscription state ───── */
  const [name, setName]           = useState('');
  const [email, setEmail]         = useState('');
  const [savingProfile, setSaving] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);

  const [invoices, setInvoices]   = useState([]);
  const [subscriptionInfo, setSub] = useState(null);
  const [loadingInv, setLoadingInv] = useState(true);
  const [canceling, setCanceling] = useState(false);

  /* ───── TOTP setup state ───── */
  const [qrUrl, setQrUrl] = useState('');
  const [token, setToken] = useState('');
  const [step, setStep]   = useState(null); // null | 'showQr'

  const nav = useNavigate();

  /* ───── profile listener ───── */
  useEffect(() => {
    if (!user) return;
    setEmail(user.email);
    setName(user.displayName || '');

    return onSnapshot(doc(db, 'users', user.uid), snap => {
      const d = snap.data() || {};
      setMfaEnabled(!!d.mfaEnabled);
    });
  }, [user]);

  /* ───── billing fetch ───── */
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const invRes = await fetch(`${API_BASE}/invoices`, {
          method : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body   : JSON.stringify({ uid: user.uid })
        });
        const { invoices = [] } = await invRes.json();
        setInvoices(invoices);

        const subRes = await fetch(`${API_BASE}/subscription`, {
          method : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body   : JSON.stringify({ uid: user.uid })
        });
        const { subscription } = await subRes.json();
        setSub(subscription || null);
      } catch (e) {
        console.error('Billing fetch error', e);
      } finally {
        setLoadingInv(false);
      }
    })();
  }, [user]);

  /* ───── helpers ───── */
  const saveProfile = async () => {
    if (!user) return;
    setSaving(true);
    await updateDoc(doc(db, 'users', user.uid), { name });
    setSaving(false);
  };

  const sendReset = async () => {
    try {
      await sendPasswordResetEmail(auth, email);
      alert('Password-reset email sent.');
    } catch (e) {
      console.error(e);
      alert('Failed to send email.');
    }
  };

  /* ─── MFA enable flow ─── */
  const beginMfaSetup = async () => {
    const res = await fetch(`${API_BASE}/mfa/setup`, {
      method : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body   : JSON.stringify({ uid: user.uid })
    });
    const { qrDataUrl } = await res.json();
    setQrUrl(qrDataUrl);
    setToken('');
    setStep('showQr');
  };

  const confirmToken = async () => {
    const res = await fetch(`${API_BASE}/mfa/verify`, {
      method : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body   : JSON.stringify({ uid: user.uid, token })
    });
    if (res.ok) {
      setStep(null);
    } else {
      alert('Invalid code, try again.');
    }
  };

  const disableMfa = async () => {
    if (!window.confirm('Disable 2-factor authentication?')) return;
    await fetch(`${API_BASE}/mfa/disable`, {
      method : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body   : JSON.stringify({ uid: user.uid })
    });
  };

  /* ─── Stripe helpers ─── */
  const openPortal = async () => {
  try {
    const res = await fetch(`${API_BASE}/create-portal-session`, {
      method : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body   : JSON.stringify({ uid: user.uid })
    });

    if (!res.ok) {
      const { message } = await res.json().catch(() => ({}));
      return alert(message || 'Could not open billing portal.');
    }

    const { url } = await res.json();
    if (!url) return alert('Portal URL missing.');
    window.location.href = url;
  } catch (err) {
    console.error(err);
    alert('Could not open billing portal.');
  }
  };

  const cancelSub = async () => {
    if (!window.confirm('Cancel at period end?')) return;
    setCanceling(true);

    const res = await fetch(`${API_BASE}/cancel-subscription`, {
      method : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body   : JSON.stringify({ uid: user.uid })
    });

    if (!res.ok) {
      const { message } = await res.json().catch(() => ({}));
      alert(message || 'Could not cancel subscription.');
      setCanceling(false);
      return;
    }

    // mark locally but keep cancelAtPeriodEnd flag
    setSub(s => ({ ...(s ?? {}), cancelAtPeriodEnd: true }));
    setCanceling(false);
  };

  /* ───── render ───── */
  return (
    <div className="account-page">
      <h1>My Account</h1>

      {/* Profile */}
      <section className="profile-section">
        <h2>Profile</h2>
        <label>
          Email:
          <input value={email} disabled />
        </label>
        <label>
          Name:
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            onBlur={saveProfile}
            disabled={savingProfile}
          />
        </label>
        <button onClick={sendReset}>Reset Password</button>
      </section>

      {/* Security */}
      <section className="security-section">
        <h2>Security</h2>
        {mfaEnabled ? (
          <button onClick={disableMfa}>Disable 2-Factor Auth</button>
        ) : (
          <button onClick={beginMfaSetup}>Enable 2-Factor Auth</button>
        )}
      </section>

      {/* Subscription */}
      <section className="plan-section">
        <h2>Subscription</h2>
        <p>
          Plan: <strong>{accountType}</strong> · Credits:{' '}
          <strong>{credits}</strong>
          <br />
          Next billing:{' '}
          <strong>
            {subscriptionInfo
              ? new Date(
                  subscriptionInfo.nextBillingDate
                ).toLocaleDateString()
              : '—'}
          </strong>
        </p>
        <button onClick={() => nav('/pricing')}>Change / Upgrade</button>
        {subscriptionInfo && (
          <>
            <button onClick={openPortal}>Manage Payment Info</button>

            {subscriptionInfo.cancelAtPeriodEnd ? (
              <span className="note">
                Cancels on&nbsp;
                {new Date(subscriptionInfo.nextBillingDate).toLocaleDateString()}
              </span>
            ) : (
              <button disabled={canceling} onClick={cancelSub}>
                {canceling ? 'Cancelling…' : 'Cancel Subscription'}
              </button>
            )}
          </>
        )}
      </section>

      {/* Invoices */}
      <section className="invoices-section">
        <h2>Invoices</h2>
        {loadingInv ? (
          <p>Loading…</p>
        ) : invoices.length === 0 ? (
          <p>No invoices.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>PDF</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id}>
                  <td>{new Date(inv.date).toLocaleDateString()}</td>
                  <td>{inv.amountFormatted}</td>
                  <td>
                    <a href={inv.pdfUrl} target="_blank" rel="noreferrer">
                      Download
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* MFA modal */}
      {step === 'showQr' && (
        <div className="mfa-modal">
          <div className="mfa-box">
            <h3>Scan QR &amp; enter first code</h3>
            <img src={qrUrl} alt="qr" />
            <input
              value={token}
              onChange={e => setToken(e.target.value)}
              placeholder="6-digit code"
            />
            <button onClick={confirmToken}>Verify</button>
            <button onClick={() => setStep(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
